import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';
import AuthLayout from '../../../components/AuthLayout.jsx';
import { getCableConsumer } from '../../../cable.js';
import { postJSON } from '../../../lib/csrf.js';
import { mergeChatMessage } from '../../../lib/chat_messages.js';

// MediaMTX v1.19.0 registers exactly two H264 variants for WebRTC ingest
// (internal/protocols/webrtc/inbound_track.go): both packetization-mode=1,
// Constrained Baseline, profile-level-id 42001f and 42e01f. Chrome offers seven
// H264 sender variants — the other five (packetization-mode=0, Main 4d001f,
// High 640034) give MediaMTX nothing to match.
const MEDIAMTX_H264_PROFILE_LEVEL_IDS = ['42001f', '42e01f'];

function is_mediamtx_compatible_h264(codec) {
  const fmtp = codec.sdpFmtpLine || '';
  return /video\/H264/i.test(codec.mimeType) &&
    fmtp.includes('packetization-mode=1') &&
    MEDIAMTX_H264_PROFILE_LEVEL_IDS.some((id) => fmtp.includes(`profile-level-id=${id}`));
}

// Why publish H264 at all: Chrome's default is VP8, and VP8 is the one video
// codec MediaMTX cannot put anywhere except live WebRTC. Its HLS muxer
// (internal/protocols/hls/from_stream.go) and both recorders
// (internal/recorder/format_mpegts.go, format_fmp4.go) handle H264/H265/etc but
// not VP8, so a VP8 publish logged "skipping track (VP8)" and recorded audio
// only. H264 is the only codec all four paths accept.
//
// Reorder, never filter. Deleting the non-H264 payload types from the m-line
// leaves no VP8 to fall back to when MediaMTX matches no profile, and video
// vanishes. Moving the compatible entries to the front keeps VP8 usable and
// keeps rtx/red/ulpfec in the m-line.
function mediamtx_preferred_video_codecs() {
  const codecs = RTCRtpSender.getCapabilities?.('video')?.codecs;
  if (!codecs) return null;
  const preferred = codecs.filter(is_mediamtx_compatible_h264);
  if (preferred.length === 0) return null;
  return [...preferred, ...codecs.filter((c) => !is_mediamtx_compatible_h264(c))];
}

// Minimal WHIP (WebRTC-HTTP Ingestion Protocol) client.
// Spec: https://datatracker.ietf.org/doc/draft-ietf-wish-whip/
// Returns { pc, stop } that DELETE's the WHIP resource to end the session.
async function startWhip({ url, streamKey, stream, iceServers, onStateChange }) {
  const pc = new RTCPeerConnection({ iceServers });

  // Explicit sendonly transceivers rather than addTrack: the video sender has
  // to exist before createOffer, because setCodecPreferences only shapes the
  // next offer generated from that transceiver.
  const audio_track = stream.getAudioTracks()[0];
  const video_track = stream.getVideoTracks()[0];
  if (audio_track) pc.addTransceiver(audio_track, { direction: 'sendonly', streams: [stream] });
  if (video_track) {
    const video_transceiver = pc.addTransceiver(video_track, { direction: 'sendonly', streams: [stream] });
    const preferred = mediamtx_preferred_video_codecs();
    if (!preferred) {
      // Safari/Firefox builds without a matching profile still publish fine on
      // the live path; only recording and HLS stay audio-only.
      console.warn('[WHIP] no MediaMTX-compatible H264 available — publishing the browser default');
    } else if (typeof video_transceiver.setCodecPreferences === 'function') {
      try {
        video_transceiver.setCodecPreferences(preferred);
      } catch (e) {
        // Never fail the broadcast over a codec preference.
        console.warn('[WHIP] setCodecPreferences failed — publishing the browser default', e);
      }
    }
  }

  pc.oniceconnectionstatechange = () => onStateChange?.(pc.iceConnectionState);

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Wait for ICE gathering to complete (or short timeout) so SDP includes
  // candidates — simpler than trickle ICE for a v1 implementation.
  await new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve();
    const t = setTimeout(resolve, 1500);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') { clearTimeout(t); resolve(); }
    });
  });

  const fetchOpts = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/sdp',
      // MediaMTX external auth forwards Basic credentials as user/password.
      // Our Rails authorize_publish accepts the stream key in the password field.
      'Authorization': 'Basic ' + btoa(`any:${streamKey}`),
    },
    body: pc.localDescription.sdp,
  };
  // Right after a Coolify deploy, the first OPTIONS preflight can hit the
  // reverse proxy before the new MediaMTX container is fully routed, and the
  // proxy returns an error without CORS headers — the browser surfaces this
  // as a CORS failure (TypeError). Retry once after a short delay so the
  // user's first "Go live" click doesn't fail.
  let res;
  try {
    res = await fetch(url, fetchOpts);
  } catch (e) {
    if (!(e instanceof TypeError)) { pc.close(); throw e; }
    await new Promise((r) => setTimeout(r, 750));
    res = await fetch(url, fetchOpts);
  }
  if (!res.ok) {
    pc.close();
    throw new Error(`WHIP server rejected the offer (HTTP ${res.status}).`);
  }
  const answerSdp = await res.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

  const resourceUrl = res.headers.get('Location');
  return {
    pc,
    stop: async () => {
      try {
        if (resourceUrl) {
          const absolute = new URL(resourceUrl, url).toString();
          await fetch(absolute, { method: 'DELETE' });
        }
      } catch (_) { /* ignore */ }
      // Do NOT call s.track.stop() — sender tracks are the same MediaStreamTrack
      // objects driving the local meter. Stopping them blanks the meter.
      // pc.close() releases the WebRTC ownership; the page's unmount effect
      // is responsible for actually stopping the tracks when the user leaves.
      pc.close();
    },
  };
}

function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

function StatusPill({ state }) {
  const cfg = state === 'live'
    ? { cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500 animate-pulse', label: 'LIVE' }
    : state === 'connecting'
      ? { cls: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500 animate-pulse', label: 'Connecting…' }
      : state === 'error'
        ? { cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', label: 'Error' }
        : { cls: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400', label: 'Idle' };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// Live mic level meter driven by a Web Audio AnalyserNode tapping the local
// mic stream. Gives the host a visible "yes my mic is picking up sound"
// indicator since there's no video preview to confirm against. This taps the
// LOCAL (publisher-side) stream, which is unrelated to viewer playback.
function MicLevelMeter({ stream }) {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream) return;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);

    const buf = new Uint8Array(analyser.fftSize);
    let raf;
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i] - 128);
        if (v > peak) peak = v;
      }
      setLevel(Math.min(1, peak / 96));
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      try { source.disconnect(); } catch (_) { /* ignore */ }
      ctx.close();
    };
  }, [stream]);

  const pct = Math.round(level * 100);
  const color = level > 0.85 ? 'bg-rose-500' : level > 0.5 ? 'bg-amber-400' : 'bg-emerald-500';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span className="font-semibold tracking-wider uppercase">Mic level</span>
        <span className="font-mono tabular-nums text-gray-500">{pct}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-75`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// Public Google STUN: enough for "everyone on a residential ISP", not enough
// for VPNs/symmetric NATs — those need the TURN entries returned by the
// ice_credentials endpoint below.
const STUN_FALLBACK = [{ urls: 'stun:stun.l.google.com:19302' }];

async function fetchIceServers(endpoint) {
  if (!endpoint) return STUN_FALLBACK;
  try {
    const res = await postJSON(endpoint);
    if (!res.ok) return STUN_FALLBACK;
    const data = await res.json();
    const servers = Array.isArray(data?.ice_servers) ? data.ice_servers : [];
    return servers.length > 0 ? [...servers, ...STUN_FALLBACK] : STUN_FALLBACK;
  } catch (_) {
    return STUN_FALLBACK;
  }
}

function fmtChatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// Host side of the webinar-wide public chat. Same stream and same ordering the
// attendees see.
function HostChat({ webinarId, chat }) {
  const [messages, setMessages] = useState(() => chat?.messages || []);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarChatChannel', webinar_id: webinarId },
      {
        received: (data) => {
          setMessages((prev) => mergeChatMessage(prev, data));
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = useCallback(async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await postJSON(chat.post_endpoint, { body });
      if (!res.ok) throw new Error('Could not send message.');
      // Show the sender their own message immediately rather than waiting on
      // the echo, which may be delayed or never arrive. A malformed 2xx must
      // reach the error handler rather than clear the draft into nothing.
      const message = await res.json();
      if (!message?.id) throw new Error('Could not send message.');
      setMessages((prev) => mergeChatMessage(prev, message));
      setDraft('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [draft, sending, chat?.post_endpoint]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800">Webinar chat</p>
        <p className="text-xs text-gray-500 mt-0.5">Everyone in this webinar sees these messages.</p>
      </div>

      <div ref={scrollerRef} data-testid="chat-messages" className="h-[22rem] overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
        {messages.length === 0 ? (
          <p className="text-xs text-gray-500 text-center pt-8">No messages yet.</p>
        ) : (
          messages.map((m) => {
            const fromHost = m.sender_role === 'host';
            return (
              <div key={m.id} data-testid="chat-message" className="flex justify-start">
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  fromHost ? 'bg-indigo-50 border border-indigo-200 text-gray-900' : 'bg-white border border-gray-200 text-gray-800'
                }`}>
                  <p
                    data-testid="chat-sender"
                    className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${
                      fromHost ? 'text-indigo-700' : 'text-gray-500'
                    }`}
                  >
                    {fromHost ? 'Host' : m.sender_name}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  <p className="text-[10px] mt-0.5 tabular-nums text-gray-400">
                    {fmtChatTime(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="p-3 border-t border-gray-100 flex items-end gap-2 bg-white">
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Message everyone…"
          disabled={sending}
          className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Send
        </button>
      </form>
      {error && <p className="px-4 pb-3 text-xs text-rose-600">{error}</p>}
    </div>
  );
}

// Private producer–instructor conversation. A separate stream, a separate
// table, and a deliberately different palette and wording from HostChat above,
// because the one mistake that matters here is typing a staff note into the
// attendee-visible chat.
function StaffChat({ webinarId, staffChat, viewerRole }) {
  const [messages, setMessages] = useState(() => staffChat?.messages || []);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  const counterpart = viewerRole === 'producer' ? 'instructor' : 'producer';
  const noInstructor = staffChat?.instructor_assigned === false;

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarStaffChatChannel', webinar_id: webinarId },
      {
        received: (data) => {
          setMessages((prev) => mergeChatMessage(prev, data));
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinarId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = useCallback(async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await postJSON(staffChat.post_endpoint, { body });
      if (!res.ok) throw new Error('Could not send message.');
      const message = await res.json();
      if (!message?.id) throw new Error('Could not send message.');
      setMessages((prev) => mergeChatMessage(prev, message));
      setDraft('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [draft, sending, staffChat?.post_endpoint]);

  return (
    <div data-testid="staff-chat" className="bg-white border-2 border-amber-300 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50">
        <p className="text-sm font-semibold text-amber-900">Private staff chat</p>
        <p className="text-xs text-amber-800 mt-0.5">
          Producer and instructor only. Attendees never see these messages.
        </p>
        {noInstructor && (
          <p data-testid="staff-chat-no-instructor" className="text-xs font-semibold text-amber-900 mt-1.5">
            No instructor is assigned to this webinar yet, so nobody is reading this side of the conversation.
          </p>
        )}
      </div>

      <div ref={scrollerRef} data-testid="staff-chat-messages" className="h-56 overflow-y-auto px-4 py-3 space-y-2 bg-amber-50/40">
        {messages.length === 0 ? (
          <p className="text-xs text-amber-800 text-center pt-6">No private messages yet.</p>
        ) : (
          messages.map((m) => {
            const fromProducer = m.sender_role === 'producer';
            return (
              <div key={m.id} data-testid="staff-chat-message" className="flex justify-start">
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  fromProducer ? 'bg-amber-100 border border-amber-300 text-gray-900' : 'bg-white border border-amber-200 text-gray-800'
                }`}>
                  <p
                    data-testid="staff-chat-sender"
                    className="text-[10px] font-semibold uppercase tracking-wide mb-0.5 text-amber-800"
                  >
                    {m.sender_name}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  <p className="text-[10px] mt-0.5 tabular-nums text-amber-700/80">
                    {fmtChatTime(m.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="p-3 border-t border-amber-200 flex items-end gap-2 bg-white">
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={`Private message to the ${counterpart}…`}
          disabled={sending}
          className="flex-1 min-w-0 resize-none rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Post privately
        </button>
      </form>
      {error && <p data-testid="staff-chat-error" className="px-4 pb-3 text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function SlidePresenter({
  sectionId,
  slideDeck,
  bioPage,
  showBioSlide,
  currentPage: requested,
  slidePageEndpoint,
  toggleBioSlideEndpoint,
  onPageAdvance,
  onControlError,
}) {
  // The bio-slide toggle only makes sense when the instructor has actually
  // uploaded a bio (server-side `bio_page` is set). Otherwise flipping the
  // flag has no visible effect and we hide the button entirely.
  const bioAvailable = !!bioPage;
  const pages = React.useMemo(() => {
    const list = [];
    if (bioAvailable && showBioSlide) list.push(bioPage);
    (slideDeck?.pages || []).forEach((p) => list.push(p));
    return list;
  }, [bioAvailable, showBioSlide, bioPage, slideDeck?.pages]);
  const totalPages = pages.length;
  const minPage = pages[0]?.page ?? 1;
  const maxPage = pages[pages.length - 1]?.page ?? 1;
  const currentPage = Math.max(minPage, Math.min(requested ?? minPage, maxPage));

  const sendPageChange = useCallback(async (page) => {
    if (!slidePageEndpoint) return;
    try {
      const res = await postJSON(slidePageEndpoint, { page, section_id: sectionId });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onControlError?.(null);
    } catch (_e) {
      onControlError?.('Could not advance the slide — attendees are still on the previous one. Try again.');
    }
  }, [slidePageEndpoint, sectionId, onControlError]);

  const toggleBioSlide = useCallback(async () => {
    if (!toggleBioSlideEndpoint) return;
    try {
      const res = await postJSON(toggleBioSlideEndpoint, { section_id: sectionId });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onControlError?.(null);
    } catch (_e) {
      onControlError?.('Could not toggle the bio slide — attendees still see the previous view. Try again.');
    }
  }, [toggleBioSlideEndpoint, sectionId, onControlError]);

  const jumpTo = useCallback((page) => {
    if (totalPages === 0) return;
    const clamped = Math.max(minPage, Math.min(page, maxPage));
    onPageAdvance?.(clamped);           // optimistic local: parent owns the state
    sendPageChange(clamped);            // persist + broadcast
  }, [totalPages, minPage, maxPage, sendPageChange, onPageAdvance]);

  const next = useCallback(() => jumpTo(currentPage + 1), [currentPage, jumpTo]);
  const prev = useCallback(() => jumpTo(currentPage - 1), [currentPage, jumpTo]);

  // Keyboard navigation — left/right arrows. Ignore when typing in an input.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const deckAttached = slideDeck?.attached || (bioAvailable && showBioSlide);
  const deckReady = slideDeck?.attached ? slideDeck.ready : (bioAvailable && showBioSlide);
  if (!deckAttached) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <p className="text-sm text-gray-500">
          No slide deck uploaded for this section. Edit the webinar to upload a
          PDF — viewers will see it here once it&apos;s processed.
        </p>
      </div>
    );
  }

  if (!deckReady) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <p className="text-sm text-gray-700 font-semibold">Processing slide deck…</p>
        <p className="text-xs text-gray-500 mt-1">Rasterizing pages in the background. Refresh in a few seconds.</p>
      </div>
    );
  }

  const currentImage = pages.find((p) => p.page === currentPage) || pages[0];
  const isBioSlide = currentPage === 0;
  const deckTotal = minPage === 0 ? maxPage : totalPages;
  const pageLabel = isBioSlide ? 'Instructor bio slide' : `Page ${currentPage} of ${deckTotal}`;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-700">Slide deck</p>
        <div className="flex items-center gap-3">
          <p className="text-xs font-mono tabular-nums text-gray-500">{pageLabel}</p>
          {toggleBioSlideEndpoint && bioAvailable && (
            <button
              type="button"
              onClick={toggleBioSlide}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold border transition ${
                showBioSlide
                  ? 'border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
              title="Show or hide the instructor bio slide for this session"
            >
              {showBioSlide ? 'Hide bio slide' : 'Show bio slide'}
            </button>
          )}
        </div>
      </div>

      <div className="relative w-full bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center" style={{ aspectRatio: '4 / 3' }}>
        {currentImage && (
          <img
            src={currentImage.url}
            alt={isBioSlide ? 'Instructor bio slide' : `Slide ${currentPage}`}
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={prev}
          disabled={currentPage <= minPage}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          ← Previous
        </button>
        <span className="text-xs text-gray-400">Use ← → keys to navigate</span>
        <button
          type="button"
          onClick={next}
          disabled={currentPage >= maxPage}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          Next →
        </button>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {pages.map((p) => {
            const isActive = p.page === currentPage;
            const label = p.page === 0 ? 'Bio' : `Page ${p.page}`;
            return (
              <button
                key={p.page}
                type="button"
                onClick={() => jumpTo(p.page)}
                className={`shrink-0 rounded-md overflow-hidden border-2 transition ${
                  isActive ? 'border-indigo-500 ring-2 ring-indigo-500/30' : 'border-gray-200 hover:border-gray-300'
                }`}
                title={`Jump to ${label.toLowerCase()}`}
              >
                <img
                  src={p.url}
                  alt={p.page === 0 ? 'Bio slide thumbnail' : `Thumbnail ${p.page}`}
                  className="block w-20 h-14 object-cover bg-white"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Polls auto-close after 2 minutes (server-enforced via WebinarPollAutoCloseJob).
// Hook returns remaining seconds for the host's countdown UI.
function usePollCountdown(closesAt) {
  const target = closesAt ? new Date(closesAt).getTime() : null;
  const compute = () => (target ? Math.max(0, Math.ceil((target - Date.now()) / 1000)) : null);
  const [remaining, setRemaining] = useState(compute);
  useEffect(() => {
    if (!target) { setRemaining(null); return; }
    setRemaining(compute());
    const id = setInterval(() => setRemaining(compute()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return remaining;
}

function formatCountdown(seconds) {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function PollCountdown({ closesAt }) {
  const remaining = usePollCountdown(closesAt);
  if (remaining == null) return null;
  return (
    <span className="text-xs font-semibold tabular-nums text-indigo-700">
      Auto-closes in {formatCountdown(remaining)}
    </span>
  );
}

// Host-side polls panel. Lists every pre-created poll, lets the host open one
// poll at a time and close the active one, and renders live aggregate counts
// (with a per-registrant breakdown disclosure). Subscribes to
// WebinarPollChannel so counters tick in as viewer responses land.
function PollControl({ webinarId, polls: initialPolls, currentPollId: initialCurrentPollId, endpoints }) {
  const [polls, setPolls] = useState(() => initialPolls || []);
  const [currentPollId, setCurrentPollId] = useState(initialCurrentPollId || null);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  // One reducer for every source of truth about a poll: the ActionCable echo
  // AND the open/close HTTP responses, which the server serializes with the
  // exact same payload builder. Applying an event twice is a no-op, so the
  // click-then-echo sequence settles on one state instead of two subtly
  // different ones — and the host sees the row change the moment the server
  // accepts the action rather than waiting on the websocket round trip.
  const applyPollEvent = useCallback((data) => {
    if (!data?.event) return;

    if (data.event === 'open' && data.poll?.id) {
      setCurrentPollId(data.poll.id);
      setPolls((prev) =>
        prev.map((p) => (p.id === data.poll.id ? {
          ...p,
          opened_at: data.poll.opened_at || p.opened_at,
          closes_at: data.poll.closes_at || null,
          closed_at: null,
        } : p)),
      );
    } else if (data.event === 'close' && data.poll_id) {
      setCurrentPollId((id) => (id === data.poll_id ? null : id));
      setPolls((prev) =>
        prev.map((p) => {
          if (p.id !== data.poll_id) return p;
          return {
            ...p,
            closed_at: data.closed_at || p.closed_at || new Date().toISOString(),
            closes_at: null,
            // A close event carries final tallies; a bare one (no counts key)
            // must not wipe counters we already have.
            counts: data.counts || p.counts || {},
            total: data.total ?? p.total ?? 0,
          };
        }),
      );
    } else if (data.event === 'response' && data.poll_id) {
      setPolls((prev) =>
        prev.map((p) =>
          p.id === data.poll_id ? { ...p, counts: data.counts || {}, total: data.total ?? 0 } : p,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarPollChannel', webinar_id: webinarId },
      { received: applyPollEvent },
    );
    return () => sub.unsubscribe();
  }, [webinarId, applyPollEvent]);

  // Returns the parsed body on success. On failure throws an Error carrying the
  // server's stable `reason` code and any authoritative state it sent back, so
  // callers can map to real copy instead of echoing a status number.
  const post = useCallback(async (url, body) => {
    const res = await postJSON(url, body);
    const payload = await res.json().catch(() => null);
    if (res.ok) return payload;
    const err = new Error(payload?.error || 'request_failed');
    err.reason = payload?.error || null;
    err.status = res.status;
    err.payload = payload;
    throw err;
  }, []);

  const openPoll = useCallback(async (pollId) => {
    if (busyId) return;
    setError(null);
    setBusyId(pollId);
    try {
      applyPollEvent(await post(endpoints.open, { poll_id: pollId }));
    } catch (e) {
      if (e.reason === 'conflict') {
        // Trust the server about which poll is actually open — the local id may
        // be why we guessed wrong in the first place.
        if (e.payload?.current_poll_id) setCurrentPollId(e.payload.current_poll_id);
        setError('Another poll is already open. Close it before opening this one.');
      } else if (e.reason === 'cross_section') {
        setError('That poll belongs to another session. Switch to its session first.');
      } else if (e.reason === 'not_found') {
        setError('That poll is no longer available.');
      } else {
        setError('Could not open the poll. Check your connection and try again.');
      }
    } finally {
      setBusyId(null);
    }
  }, [busyId, endpoints?.open, post, applyPollEvent]);

  const closePoll = useCallback(async (pollId) => {
    if (busyId) return;
    setError(null);
    setBusyId(pollId);
    try {
      applyPollEvent(await post(endpoints.close, { poll_id: pollId }));
    } catch (e) {
      if (e.reason === 'already_closed') {
        // Lost a race with the auto-close job or another host tab. The server
        // sent the authoritative close payload — reconcile silently rather than
        // alarming the host about something that already went the way they wanted.
        applyPollEvent(e.payload);
      } else if (e.reason === 'not_open') {
        setError('That poll is not open.');
      } else if (e.reason === 'not_found') {
        setError('That poll is no longer available.');
      } else {
        setError('Could not close the poll. Check your connection and try again.');
      }
    } finally {
      setBusyId(null);
    }
  }, [busyId, endpoints?.close, post, applyPollEvent]);

  if (!polls.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-800">Live polls</p>
        <p className="text-xs text-gray-500 mt-1">
          No polls set up yet. Edit the webinar to add poll questions and options.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-semibold text-gray-800">Live polls</p>
        {/* Announced on change so a host using a screen reader hears the poll
            open/close without having to hunt for the row that moved. */}
        <p className="text-xs text-gray-500" role="status" aria-live="polite">
          {currentPollId ? 'One poll is open.' : 'No poll is open.'}
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
        >
          {error}
        </div>
      )}

      <div className="mt-3 space-y-3">
        {polls.map((poll) => {
          const isCurrent = currentPollId === poll.id;
          const isClosed = !!poll.closed_at;
          const total = poll.total || 0;
          const counts = poll.counts || {};
          const breakdown = poll.responder_breakdown || [];
          const showBreakdown = expanded[poll.id];
          return (
            <div
              key={poll.id}
              data-testid={`poll-row-${poll.id}`}
              className={`rounded-xl border p-4 transition ${
                isCurrent
                  ? 'border-indigo-300 bg-indigo-50/40'
                  : isClosed
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {isCurrent ? 'Open' : isClosed ? 'Closed' : 'Ready'}
                    </p>
                    {isCurrent && <PollCountdown closesAt={poll.closes_at} />}
                  </div>
                  <p className="mt-0.5 text-sm font-semibold text-gray-900">{poll.question}</p>
                </div>
                <div className="shrink-0">
                  {isCurrent ? (
                    <button
                      type="button"
                      onClick={() => closePoll(poll.id)}
                      disabled={busyId === poll.id}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {busyId === poll.id ? 'Closing…' : 'Close poll'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPoll(poll.id)}
                      disabled={busyId === poll.id || (currentPollId && currentPollId !== poll.id)}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {busyId === poll.id ? 'Opening…' : isClosed ? 'Reopen poll' : 'Open poll'}
                    </button>
                  )}
                </div>
              </div>

              <ul className="mt-3 space-y-1.5">
                {poll.options.map((opt) => {
                  const c = counts[opt.id] || 0;
                  const pct = total > 0 ? Math.round((c / total) * 100) : 0;
                  return (
                    <li key={opt.id} className="text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-gray-800">{opt.body}</span>
                        <span className="text-xs tabular-nums text-gray-500">{c} ({pct}%)</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>{total} {total === 1 ? 'response' : 'responses'}</span>
                {breakdown.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [poll.id]: !e[poll.id] }))}
                    className="font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                  >
                    {showBreakdown ? 'Hide responders' : 'Show responders'}
                  </button>
                )}
              </div>

              {showBreakdown && breakdown.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white text-xs">
                  {breakdown.map((b) => {
                    const opt = poll.options.find((o) => o.id === b.option_id);
                    return (
                      <li
                        key={b.registration_id}
                        className="flex items-baseline justify-between border-b border-gray-100 px-3 py-1.5 last:border-b-0"
                      >
                        <span className="text-gray-800">{b.user_name}</span>
                        <span className="text-gray-500">{opt?.body || '—'}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Host-side free-text questions panel. Sibling of PollControl: lists every
// pre-created question, lets the host open one at a time and close the active
// one, and renders viewers' open-text answers as they stream in over
// WebinarQuestionChannel. A late-joining host replays answers from the initial
// props, then live answers append/replace by registration.
function QuestionControl({ webinarId, questions: initialQuestions, currentQuestionId: initialCurrentQuestionId, endpoints }) {
  const [questions, setQuestions] = useState(() => initialQuestions || []);
  const [currentQuestionId, setCurrentQuestionId] = useState(initialCurrentQuestionId || null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const autoClosedRef = useRef(new Set());

  // Merge an incoming answer into a question's answer list, replacing any prior
  // answer from the same registration (viewers can update until close).
  const mergeAnswer = useCallback((list, answer) => {
    const idx = list.findIndex((a) => a.registration_id === answer.registration_id);
    if (idx === -1) return [...list, answer];
    const next = [...list];
    next[idx] = answer;
    return next;
  }, []);

  // Sibling of PollControl's applyPollEvent — one idempotent reducer shared by
  // the ActionCable echo and the open/close HTTP responses (identical payloads,
  // built by WebinarQuestionChannel). Answers already collected are never
  // discarded by a lifecycle event.
  const applyQuestionEvent = useCallback((data) => {
    if (!data?.event) return;

    if (data.event === 'open' && data.question?.id) {
      // Re-arm auto-close after reopening this question.
      autoClosedRef.current.delete(data.question.id);
      setCurrentQuestionId(data.question.id);
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === data.question.id
            ? {
                ...q,
                opened_at: data.question.opened_at || q.opened_at,
                closes_at: data.question.closes_at || q.closes_at || null,
                closed_at: null,
              }
            : q,
        ),
      );
    } else if (data.event === 'close' && data.question_id) {
      setCurrentQuestionId((id) => (id === data.question_id ? null : id));
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === data.question_id
            ? { ...q, closed_at: data.closed_at || q.closed_at || new Date().toISOString() }
            : q,
        ),
      );
    } else if (data.event === 'response' && data.question_id && data.response) {
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === data.question_id ? { ...q, answers: mergeAnswer(q.answers || [], data.response) } : q,
        ),
      );
    }
  }, [mergeAnswer]);

  useEffect(() => {
    if (!webinarId) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarQuestionChannel', webinar_id: webinarId },
      { received: applyQuestionEvent },
    );
    return () => sub.unsubscribe();
  }, [webinarId, applyQuestionEvent]);

  const post = useCallback(async (url, body) => {
    const res = await postJSON(url, body);
    const payload = await res.json().catch(() => null);
    if (res.ok) return payload;
    const err = new Error(payload?.error || 'request_failed');
    err.reason = payload?.error || null;
    err.status = res.status;
    err.payload = payload;
    throw err;
  }, []);

  const openQuestion = useCallback(async (questionId) => {
    if (busyId) return;
    setError(null);
    setBusyId(questionId);
    try {
      applyQuestionEvent(await post(endpoints.open, { question_id: questionId }));
    } catch (e) {
      if (e.reason === 'conflict') {
        if (e.payload?.current_question_id) setCurrentQuestionId(e.payload.current_question_id);
        setError('Another question is already being shown. Close it first.');
      } else if (e.reason === 'cross_section') {
        setError('That question belongs to another session. Switch to its session first.');
      } else if (e.reason === 'not_found') {
        setError('That question is no longer available.');
      } else {
        setError('Could not show the question. Check your connection and try again.');
      }
    } finally {
      setBusyId(null);
    }
  }, [busyId, endpoints?.open, post, applyQuestionEvent]);

  const closeQuestion = useCallback(async (questionId) => {
    if (busyId) return;
    setError(null);
    setBusyId(questionId);
    try {
      applyQuestionEvent(await post(endpoints.close, { question_id: questionId }));
    } catch (e) {
      if (e.reason === 'already_closed') {
        // Routinely hit by the countdown auto-close below racing a cable close
        // or another host tab. Reconcile from the server's payload and stay quiet.
        applyQuestionEvent(e.payload);
      } else if (e.reason === 'not_open') {
        setError('That question is not open.');
      } else if (e.reason === 'not_found') {
        setError('That question is no longer available.');
      } else {
        setError('Could not close the question. Check your connection and try again.');
      }
    } finally {
      setBusyId(null);
    }
  }, [busyId, endpoints?.close, post, applyQuestionEvent]);

  // While a question is open, tick once a second to drive the countdown badge
  // and auto-close it when the 2-minute window elapses (best-effort, host-side;
  // the server also rejects viewer answers past the deadline).
  const openQuestionRow = questions.find((q) => q.id === currentQuestionId);
  const openClosesAt = openQuestionRow?.closes_at ? new Date(openQuestionRow.closes_at).getTime() : null;
  useEffect(() => {
    if (!currentQuestionId) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [currentQuestionId]);
  useEffect(() => {
    if (!currentQuestionId || !openClosesAt) return;
    if (now < openClosesAt) return;
    if (autoClosedRef.current.has(currentQuestionId)) return;
    autoClosedRef.current.add(currentQuestionId);
    closeQuestion(currentQuestionId);
  }, [currentQuestionId, openClosesAt, now, closeQuestion]);

  if (!questions.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-800">Live questions</p>
        <p className="text-xs text-gray-500 mt-1">
          No questions set up yet. Edit the webinar to add open-ended questions.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-semibold text-gray-800">Live questions</p>
        <p className="text-xs text-gray-500" role="status" aria-live="polite">
          {currentQuestionId ? 'One question is open.' : 'No question is open.'}
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
        >
          {error}
        </div>
      )}

      <div className="mt-3 space-y-3">
        {questions.map((question) => {
          const isCurrent = currentQuestionId === question.id;
          const isClosed = !!question.closed_at;
          const answers = question.answers || [];
          const secondsLeft =
            isCurrent && question.closes_at
              ? Math.max(0, Math.round((new Date(question.closes_at).getTime() - now) / 1000))
              : null;
          return (
            <div
              key={question.id}
              data-testid={`question-row-${question.id}`}
              className={`rounded-xl border p-4 transition ${
                isCurrent
                  ? 'border-indigo-300 bg-indigo-50/40'
                  : isClosed
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {isCurrent ? 'Open' : isClosed ? 'Closed' : 'Ready'}
                    {secondsLeft != null && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                          secondsLeft === 0 ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {secondsLeft === 0
                          ? "Time's up"
                          : `${Math.floor(secondsLeft / 60)}:${(secondsLeft % 60).toString().padStart(2, '0')} left`}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-900">{question.prompt}</p>
                </div>
                <div className="shrink-0">
                  {isCurrent ? (
                    <button
                      type="button"
                      onClick={() => closeQuestion(question.id)}
                      disabled={busyId === question.id}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {busyId === question.id ? 'Closing…' : 'Close question'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openQuestion(question.id)}
                      disabled={busyId === question.id || (currentQuestionId && currentQuestionId !== question.id)}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {busyId === question.id ? 'Opening…' : isClosed ? 'Reopen question' : 'Open question'}
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3">
                <p className="text-xs text-gray-500">
                  {answers.length} {answers.length === 1 ? 'answer' : 'answers'}
                </p>
                {answers.length > 0 && (
                  <ul className="mt-2 max-h-56 space-y-2 overflow-y-auto">
                    {answers.map((a) => (
                      <li
                        key={a.registration_id}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                      >
                        <p className="text-xs font-semibold text-gray-700">{a.user_name}</p>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-800">{a.answer}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Turns a getUserMedia rejection into something the host can act on: classify
// by DOMException name and say only what the name actually proves.
//
// NotAllowedError deliberately does NOT claim the instructor clicked Deny — the
// same name covers a dismissed prompt, an origin blocked earlier, and a context
// where policy forbids capture outright. The copy names the outcome (the
// browser didn't allow it) and points at the fix, without inventing a cause.
//
// The device-specific names get the same treatment. One getUserMedia call asks
// for camera AND mic together, so the rejection never says which one failed:
// NotFoundError does not prove both are missing, NotReadableError does not
// identify the busy device, and OverconstrainedError can come from the camera
// or a resolution/frame-rate constraint just as easily as the mic. Each message
// therefore points at both devices and lets the instructor find the bad one.
function describe_media_failure(e) {
  switch (e?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Your browser did not allow camera and microphone access. Check the camera and microphone permissions for this site in your browser’s site settings, then try again.';
    case 'NotFoundError':
      return 'A required camera or microphone was not found. Connect both devices, then try again.';
    case 'NotReadableError':
      return 'Your camera or microphone could not be started — another app may be using one of them. Close anything else that uses them, then try again.';
    case 'OverconstrainedError':
      return 'A selected media device or requested setting is no longer available. Check your camera and microphone selections, then try again.';
    case 'AbortError':
      return 'The camera and microphone request was interrupted before it finished. Try again.';
    default:
      return `Could not start your camera and microphone${e?.message ? ` (${e.message})` : ''}. Try again.`;
  }
}

// Shared broadcast page used by superadmin AND instructor hosts. The two
// callers pass slightly different chrome:
//   * superadmin → full Layout (left-nav sidebar) + "Back to webinar" link
//     to /superadmin/webinars/:id
//   * instructor → chrome-less AuthLayout (no admin nav) + optional "Back to
//     dashboard" link to /instructor — instructors shouldn't be exposed to
//     the admin nav surface.
//
// Props that drive the chrome:
//   back_link: { href: string, label: string, external?: boolean } | null
//     If null, no back link is rendered. `external: true` means "target is
//     a non-Inertia page" — renders a plain <a> so the browser does a full
//     page navigation, avoiding Inertia's iframe fallback for routes that
//     return raw HTML (e.g. the instructor dashboard, which is still ERB).
//   chromeless: boolean (default false)
//     When true, render with AuthLayout (no nav) instead of Layout.
//   producer: boolean (default false)
//     Renders the control room without any publishing capability. The producer
//     route sends no `publish` prop, hence the default below.
//   staff_chat: { post_endpoint, instructor_assigned, messages } | null
//     Private producer-instructor conversation. Only the producer room and the
//     assigned instructor's page are served it; absent everywhere else, which is
//     what keeps the panel off pages whose viewer is not in that pair.
export default function WebinarBroadcast({ webinar, publish = {}, sections: initialSections = [], section_endpoints: sectionEndpoints = {}, chat, staff_chat: staffChat = null, poll_endpoints: pollEndpoints, question_endpoints: questionEndpoints, back_link: backLink = null, chromeless = false, producer = false }) {
  const streamRef = useRef(null);
  const sessionRef = useRef(null);
  const startedAtRef = useRef(null);
  const previewRef = useRef(null);

  const [mics, setMics] = useState([]);
  const [micId, setMicId] = useState('');
  const [stream, setStream] = useState(null);
  const [state, setState] = useState('idle');  // idle | connecting | live | error
  const [error, setError] = useState(null);
  // Capture state, kept separate from `state` above (which tracks the WHIP
  // publish) so that "nobody has asked for the microphone yet" is expressible.
  // 'not_requested' is the value on page load and must never look like an error.
  const [mediaStatus, setMediaStatus] = useState('not_requested');  // not_requested | requesting | ready | failed
  const [mediaError, setMediaError] = useState(null);
  // Re-entrancy guard for getUserMedia. A double-click, or Go live landing on
  // top of an in-flight Enable click, must not open a second permission prompt.
  // A ref, not state, because the check has to be synchronous.
  const capturingRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  // In-flight Stop click — disables the button so a double-click can't fire
  // two teardowns, and never latches on (always cleared in the finally below).
  const [stopping, setStopping] = useState(false);
  // Set when the intentional-stop signal to Rails failed. The publish is still
  // torn down; viewers just get the interrupted (reconnecting) treatment
  // instead of a clean "ended", so tell the host that's what they'll see.
  const [stopWarning, setStopWarning] = useState(null);
  // Presentation controls (section switch, slide advance, bio slide) are
  // server-authoritative. When one is refused the host must be told, because
  // the console's own view would otherwise keep moving while every attendee
  // stays behind.
  const [controlError, setControlError] = useState(null);

  // Sections + which one is currently active. Server-authoritative — updates
  // arrive on WebinarPresentationChannel with event=section/page/overlay.
  const [sections, setSections] = useState(initialSections);
  const [currentSectionId, setCurrentSectionId] = useState(
    webinar.current_section_id || initialSections[0]?.id || null,
  );
  const currentSection = sections.find((s) => s.id === currentSectionId) || sections[0] || null;

  const activateSection = useCallback(async (sectionId) => {
    if (!sectionEndpoints.activate || sectionId === currentSectionId) return;
    const previousSectionId = currentSectionId;
    setCurrentSectionId(sectionId);
    setControlError(null);
    try {
      const res = await postJSON(sectionEndpoints.activate, { section_id: sectionId });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (_e) {
      setCurrentSectionId(previousSectionId);
      setControlError('Could not switch sections — attendees are still on the previous one. Try again.');
    }
  }, [sectionEndpoints.activate, currentSectionId]);

  const setSectionField = useCallback((sectionId, field, value) => {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, [field]: value } : s)));
  }, []);

  // Real-time updates: section switching, page advance, bio-slide toggle.
  // Other host tabs get the same signal so they stay in lockstep with this
  // tab's actions. The audio publish is on its own WHIP session — nothing
  // here touches it.
  useEffect(() => {
    if (!webinar.id) return;
    const consumer = getCableConsumer();
    const sub = consumer.subscriptions.create(
      { channel: 'WebinarPresentationChannel', webinar_id: webinar.id },
      {
        received: (data) => {
          if (data?.event === 'page' && typeof data.page === 'number' && data.current_section_id) {
            setSectionField(data.current_section_id, 'current_slide_page', data.page);
          } else if (data?.event === 'section' && data.current_section_id) {
            setCurrentSectionId(data.current_section_id);
            if (typeof data.page === 'number') {
              setSectionField(data.current_section_id, 'current_slide_page', data.page);
            }
          } else if (data?.event === 'bio_slide' && data.section_id) {
            setSectionField(data.section_id, 'show_bio_slide', !!data.show_bio_slide);
          }
        },
      },
    );
    return () => sub.unsubscribe();
  }, [webinar.id, setSectionField]);

  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMics(devices.filter((d) => d.kind === 'audioinput'));
    } catch (_) { /* ignored */ }
  }, []);

  // The only getUserMedia call site on this page. Every caller must be a click
  // handler, never an effect: without a user gesture some browsers reject the
  // request outright instead of showing a permission prompt.
  const acquireStream = useCallback(async () => {
    if (capturingRef.current) return null;

    // No mediaDevices at all: an old browser, or a page served over plain HTTP
    // from something other than localhost. Worth separating, because "allow
    // the permission" is useless advice when the API isn't there to ask.
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaStatus('failed');
      setMediaError(
        window.isSecureContext === false
          ? 'This page must be served over HTTPS before the browser will allow camera and microphone access.'
          : 'This browser does not support camera and microphone capture. Use a current version of Chrome, Edge, Firefox or Safari.',
      );
      return null;
    }

    capturingRef.current = true;
    setMediaStatus('requesting');
    setMediaError(null);
    try {
      const audio = micId ? { deviceId: { exact: micId } } : true;
      // v1 instructor camera: the instructor must be visible, so we require
      // BOTH camera and mic. Constraints are ideal (non-mandatory) — if the
      // webcam can't hit 640×360@24 it downgrades rather than failing. There
      // is intentionally NO audio-only fallback: a failure here leaves the
      // broadcast stopped and surfaces an error instead of publishing a
      // faceless stream.
      const video = {
        width: { ideal: 640 },
        height: { ideal: 360 },
        frameRate: { ideal: 24, max: 30 },
      };
      const next = await navigator.mediaDevices.getUserMedia({ video, audio });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = next;
      setStream(next);
      // Fresh tracks default to enabled. Reset our mute state so the UI doesn't
      // claim "muted" while a brand-new mic is actually hot.
      setMuted(false);
      setMediaStatus('ready');
      await refreshDevices();
      return next;
    } catch (e) {
      // One getUserMedia call covers both devices, so the exception still can't
      // say whether the camera, the mic, or both failed — but its name does say
      // what KIND of failure it was, which is what the host needs to act on.
      setMediaStatus('failed');
      setMediaError(describe_media_failure(e));
      return null;
    } finally {
      capturingRef.current = false;
    }
  }, [micId, refreshDevices]);

  // Toggle local mic mute. We flip `enabled` on each audio track rather than
  // stopping/removing them — that keeps the WebRTC sender alive and just sends
  // silence, so unmuting is instant (no SDP renegotiation, no glitches for
  // listeners). The mic level meter taps the same tracks, so it naturally
  // drops to zero while muted.
  const toggleMute = useCallback(() => {
    const s = streamRef.current;
    if (!s) return;
    const tracks = s.getAudioTracks();
    if (tracks.length === 0) return;
    const next = !muted;
    tracks.forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  // Device switching only — this effect must never capture on mount, or an
  // instructor merely opening the room triggers a gesture-less getUserMedia.
  // `micId` starts '' and only changes when the instructor picks from a device
  // list that exists solely after a successful capture, so the guard below can
  // only be satisfied by a deliberate switch. Teardown lives in its own
  // unmount-only effect: re-running it here would stop the live WHIP session on
  // a device change.
  useEffect(() => {
    if (!micId || !streamRef.current) return;
    acquireStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micId]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    sessionRef.current?.stop();
  }, []);

  useEffect(() => {
    if (state !== 'live') return;
    const id = setInterval(() => setElapsed(Date.now() - (startedAtRef.current || Date.now())), 1000);
    return () => clearInterval(id);
  }, [state]);

  // Pipe the local stream into the self-view <video> so the host can frame
  // themselves. Muted to avoid an echo of their own mic. Only relevant when
  // the stream actually carries a camera track.
  useEffect(() => {
    const v = previewRef.current;
    if (v && v.srcObject !== stream) v.srcObject = stream;
  }, [stream]);

  const goLive = async () => {
    if (!streamRef.current) {
      const s = await acquireStream();
      if (!s) return;
    }
    // Defensive gate: the instructor camera must be visible AND audible. If the
    // acquired stream is missing either track, tear it down and refuse to go
    // live rather than publishing a faceless or silent broadcast.
    const s = streamRef.current;
    if (!s || s.getVideoTracks().length === 0 || s.getAudioTracks().length === 0) {
      s?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setStream(null);
      setMediaStatus('failed');
      setMediaError('Your camera and microphone must both be active to go live. Enable both, then try again.');
      return;
    }
    setError(null);
    setState('connecting');
    try {
      // Mint fresh TURN creds at click time so they're valid for the whole
      // session and so VPN/NAT-restricted hosts get a relay path.
      const iceServers = await fetchIceServers(publish.ice_credentials_endpoint);

      const session = await startWhip({
        url: publish.whip_url,
        streamKey: publish.stream_key,
        stream: streamRef.current,
        iceServers,
        onStateChange: (iceState) => {
          if (iceState === 'connected' || iceState === 'completed') {
            startedAtRef.current = startedAtRef.current || Date.now();
            setState('live');
          } else if (iceState === 'failed' || iceState === 'disconnected') {
            setState('error');
            setError(`WebRTC connection ${iceState}.`);
          }
        },
      });
      sessionRef.current = session;
    } catch (e) {
      setError(e.message);
      setState('error');
    }
  };

  // Order matters: tell Rails "this stop is intentional" BEFORE closing the
  // WHIP session. MediaMTX fires the same not_ready callback whether the
  // instructor stopped on purpose or their machine dropped off, so that marker
  // is the only thing that lets the server end the webinar for viewers instead
  // of parking them on "Reconnecting…".
  //
  // If the signal fails we still stop publishing — refusing to stop because a
  // bookkeeping call failed would leave the host live against their wishes.
  // The trade-off is announced in the UI: viewers see the interrupted state
  // (window stays up, reconnecting) until the room is closed, rather than a
  // clean end. Nothing here is retried; the WHIP DELETE follows either way, so
  // the button can never latch.
  const stopBroadcast = async () => {
    if (stopping) return;
    setStopping(true);
    setStopWarning(null);
    try {
      if (publish.stop_endpoint) {
        const res = await postJSON(publish.stop_endpoint);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
    } catch (_) {
      setStopWarning('Broadcast stopped, but the portal could not be told it was intentional — attendees may see “Reconnecting…” instead of the broadcast ending.');
    } finally {
      await sessionRef.current?.stop();
      sessionRef.current = null;
      startedAtRef.current = null;
      setState('idle');
      setElapsed(0);
      setStopping(false);
    }
  };

  const live = state === 'live';
  const busy = state === 'connecting';
  const hasCamera = !!stream && stream.getVideoTracks().length > 0;

  // AuthLayout strips the full admin nav for non-superadmin hosts (currently
  // the instructor flow). The body of the page is the same regardless.
  const LayoutComponent = chromeless ? AuthLayout : Layout;

  return (
    <LayoutComponent>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
        {backLink && (
          backLink.external ? (
            <a href={backLink.href} className="text-sm text-indigo-600 hover:text-indigo-700">
              ← {backLink.label}
            </a>
          ) : (
            <Link href={backLink.href} className="text-sm text-indigo-600 hover:text-indigo-700">
              ← {backLink.label}
            </Link>
          )
        )}

        {/* Sticky so mute and Go live/Stop stay reachable while presenting.
            These are the only start/stop and mute controls on the page. */}
        <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-gray-200">
          <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {producer ? 'Producing: ' : 'Broadcast: '}{webinar.title}
              </h1>
              {webinar.instructor && <p className="text-xs text-gray-500">Hosted by {webinar.instructor}</p>}
            </div>

            {!producer && (
              <>
                <div className="flex items-center gap-2 shrink-0">
                  {live && <span className="text-sm font-mono text-gray-700 tabular-nums">{fmtElapsed(elapsed)}</span>}
                  <StatusPill state={state} />
                  {muted && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-800 border-amber-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Muted
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={toggleMute}
                    disabled={!stream || busy}
                    aria-pressed={muted}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border shadow-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                      muted
                        ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                        : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {muted ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4l16 16" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9v2a3 3 0 005.12 2.12M15 11V5a3 3 0 00-5.83-1" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0M12 18v3" />
                      </svg>
                    )}
                    {muted ? 'Unmute' : 'Mute'}
                  </button>

                  {live ? (
                    <button
                      type="button"
                      onClick={stopBroadcast}
                      disabled={stopping}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {stopping ? 'Stopping…' : 'Stop broadcast'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={goLive}
                      disabled={busy}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {busy ? 'Connecting…' : 'Go live'}
                    </button>
                  )}
                </div>
              </>
            )}

            {producer && (
              <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-violet-50 text-violet-800 border-violet-200">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                Producer
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 text-sm">{error}</div>
        )}

        {controlError && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm">{controlError}</div>
        )}
        {stopWarning && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm">{stopWarning}</div>
        )}

        {/* Control room: presentation + media on the left, the interaction rail
            on the right. Collapses to one column below lg, where DOM order puts
            the presentation first and the rail after it. The grid is
            unconditional, so nothing remounts as the viewport changes. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)] gap-5 items-start">
          <div className="min-w-0 space-y-5">

          {sections.length > 1 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="mb-2 flex items-baseline justify-between">
                <p className="text-sm font-semibold text-gray-800">Sessions</p>
                <p className="text-xs text-gray-500">
                  Viewers only see the active session.
                </p>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {sections.map((s) => {
                  const active = s.id === currentSectionId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => activateSection(s.id)}
                      className={`shrink-0 rounded-lg border px-3 py-2 text-left transition ${
                        active
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Session {(sections.findIndex((x) => x.id === s.id)) + 1}{active ? ' · Live' : ''}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{s.title}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {currentSection ? (
            <SlidePresenter
              sectionId={currentSection.id}
              slideDeck={currentSection.slide_deck}
              bioPage={currentSection.bio_page}
              showBioSlide={currentSection.show_bio_slide}
              currentPage={currentSection.current_slide_page}
              slidePageEndpoint={sectionEndpoints.slide_page}
              toggleBioSlideEndpoint={sectionEndpoints.toggle_bio_slide}
              onControlError={setControlError}
              onPageAdvance={(page) => setSectionField(currentSection.id, 'current_slide_page', page)}
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <p className="text-sm text-gray-500">No sessions on this webinar yet. Edit the webinar to add one.</p>
            </div>
          )}

          {producer ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <p className="text-base font-semibold text-gray-900">Producer room</p>
              <p className="text-sm text-gray-600 mt-1">
                You are assisting behind the scenes. Slides, chat, polls and questions
                below are live for attendees. Your camera and microphone stay off —
                {webinar.instructor ? ` ${webinar.instructor}` : ' the instructor'} owns the broadcast.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-5">
              <div className="flex items-center gap-4">
                <div className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center ${muted ? 'bg-amber-50' : live ? 'bg-rose-50' : 'bg-gray-100'}`}>
                  {muted ? (
                    <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4l16 16" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9v2a3 3 0 005.12 2.12M15 11V5a3 3 0 00-5.83-1" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-.65 2.95M12 18v3M8 21h8" />
                    </svg>
                  ) : (
                    <svg className={`w-7 h-7 ${live ? 'text-rose-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0M12 18v3" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-500">
                    {stream ? (hasCamera ? 'Camera + audio broadcast' : 'Audio-only broadcast') : 'Camera + audio broadcast'}
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {!stream
                      ? 'Enable your camera and microphone to get started.'
                      : muted
                        ? (live ? 'You are muted. Viewers hear silence.' : 'Mic is muted.')
                        : (live
                            ? (hasCamera ? 'You are live. Viewers can see and hear you.' : 'You are live. Listeners can hear you.')
                            : (hasCamera ? 'Frame yourself and go live.' : 'Pick your microphone and go live.'))}
                  </p>
                </div>
                {muted && (
                  <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-800 border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Muted
                  </span>
                )}
              </div>

              {/* Self-view: local camera preview so the host can frame themselves.
                  Only shown when a camera track is present (audio-only hosts skip
                  it). This is a LOCAL preview — the published feed is streamRef. */}
              {hasCamera && (
                <div className="rounded-xl overflow-hidden bg-black shadow-sm w-full max-w-xs mx-auto">
                  <video
                    ref={previewRef}
                    autoPlay
                    playsInline
                    muted
                    className="block w-full aspect-video object-cover"
                  />
                </div>
              )}

              {/* The permission gate: present until capture succeeds, and the
                  only thing on this page that opens a browser prompt — hence a
                  button rather than an effect. Mirrors the attendee-side
                  ConsentGate so both sides ask for devices the same way. */}
              {mediaStatus !== 'ready' && (
                <div className={`rounded-xl border px-4 py-4 space-y-3 ${
                  mediaStatus === 'failed'
                    ? 'border-rose-200 bg-rose-50'
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <p className={`text-sm ${mediaStatus === 'failed' ? 'text-rose-800' : 'text-gray-700'}`}>
                    {mediaStatus === 'failed'
                      ? mediaError
                      : mediaStatus === 'requesting'
                        ? 'Waiting for you to allow camera and microphone access…'
                        : 'Your camera and microphone are not enabled yet. Your browser will ask for permission when you enable them.'}
                  </p>
                  <button
                    type="button"
                    onClick={acquireStream}
                    disabled={mediaStatus === 'requesting'}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {mediaStatus === 'requesting'
                      ? 'Requesting access…'
                      : mediaStatus === 'failed'
                        ? 'Try again'
                        : 'Enable camera and microphone'}
                  </button>
                </div>
              )}

              <MicLevelMeter stream={stream} />

              {/* Locked while live: changing micId re-acquires, which stops the
                  tracks the WHIP senders are publishing and replaces them with
                  new ones. Nothing republishes afterwards, so the host would sit
                  on a "You are live" UI while every viewer silently fell back to
                  "Reconnecting…". Switching devices mid-broadcast needs a
                  republish this page does not implement — so the control is
                  disabled rather than lying.

                  Locked while a capture is in flight for the same reason. A
                  switch made mid-request is dropped by the re-entrancy guard in
                  acquireStream, but `micId` has already moved — so the page would
                  settle back to 'ready' with the picker naming a microphone it
                  never acquired. Holding the control until the request resolves
                  keeps the selection and the live stream the same fact. */}
              <DeviceSelect
                label="Microphone"
                value={micId}
                options={mics}
                onChange={setMicId}
                disabled={busy || live || mediaStatus === 'requesting'}
                // Device labels only exist after permission is granted, so before
                // that the list is empty for a reason — say which reason rather
                // than reporting "No devices found" at a mic nobody has looked for.
                placeholder={mediaStatus === 'ready' ? 'No devices found' : 'Enable your microphone to list devices'}
                hint={live ? 'Stop the broadcast to switch microphones.' : null}
              />

            </div>
          )}
          </div>

          {/* Interaction rail: everything the host monitors while presenting.
              Bounded to a readable width and kept beside the deck so opening a
              poll no longer means scrolling past the whole chat history. */}
          <div className="min-w-0 space-y-5">
              <PollControl
            key={currentSection?.id || 'no-section'}
            webinarId={webinar.id}
            polls={currentSection?.polls || []}
            // Only surface `currentPollId` when it actually belongs to a poll in
            // this section. Otherwise a still-open poll from the previous section
            // would leave every "Open" button disabled here.
            currentPollId={(currentSection?.polls || []).some((p) => p.id === webinar.current_poll_id) ? webinar.current_poll_id : null}
            endpoints={pollEndpoints || {}}
          />

          <QuestionControl
            key={`q-${currentSection?.id || 'no-section'}`}
            webinarId={webinar.id}
            questions={currentSection?.questions || []}
            currentQuestionId={(currentSection?.questions || []).some((q) => q.id === webinar.current_question_id) ? webinar.current_question_id : null}
            endpoints={questionEndpoints || {}}
          />

            {chat && <HostChat webinarId={webinar.id} chat={chat} />}

            {staffChat && (
              <StaffChat
                webinarId={webinar.id}
                staffChat={staffChat}
                viewerRole={producer ? 'producer' : 'instructor'}
              />
            )}
          </div>
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p>Listeners will hear the stream in the room at <code>/webinars/{webinar.id}/room</code> with sub-second WebRTC delivery (HLS fallback for restricted networks).</p>
          {!producer && <p>Microphone permission only persists for this browser; you'll be re-prompted in a private window.</p>}
        </div>
      </div>
    </LayoutComponent>
  );
}

function DeviceSelect({ label, value, options, onChange, disabled, hint, placeholder = 'No devices found' }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-wider text-gray-600 uppercase mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || options.length === 0}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
      >
        {options.length === 0 && <option value="">{placeholder}</option>}
        {options.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>{d.label || `${label} ${i + 1}`}</option>
        ))}
      </select>
      {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    </label>
  );
}
